// Bulk-selection bar — keeps the selection count, the select-all box,
// and the bar's visibility in step. Targets a [data-bulk-root] wrapper
// holding [data-bulk-select-all], [data-bulk-row] checkboxes, the
// [data-bulk-bar] itself, and a [data-bulk-count] readout.
//
// The readout's noun comes from the markup, so the same bar counts
// prospects on the pipeline and clients on the portfolio.
//
// The checkboxes submit as `prospect_id` on the bar's own form, so the
// selection travels with the request without this file assembling it,
// and each box sits in a <label> filling its cell so the padding
// toggles it natively. All this does is show the bar and keep the
// header box honest.
//
// A list that pages may carry [data-bulk-all], a disabled hidden input
// that widens the selection to every row the filters match. Its offer,
// [data-bulk-all-offer], shows once the whole page is checked and
// toggles the input; unchecking any row withdraws it. A button with
// data-bulk-confirm-title gets its confirm title re-worded with the
// selection's size in place of {selection}.
//
// [data-bulk-export] posts the selection from a plain form of its own,
// so the browser downloads the file instead of htmx swapping it in. A
// bulk:sync event, which bulk_actions.js sends when the picked action
// changes, re-runs the sync.

import { onceGuard } from "./bind_once.js";

const firstBind = onceGuard();

export function initBulkSelect(root = document) {
  const wrappers = root.querySelectorAll("[data-bulk-root]");

  wrappers.forEach((wrapper) => {
    if (!firstBind(wrapper)) return;

    const bar = wrapper.querySelector("[data-bulk-bar]");
    const count = wrapper.querySelector("[data-bulk-count]");
    const selectAll = wrapper.querySelector("[data-bulk-select-all]");
    const everyRow = wrapper.querySelector("[data-bulk-all]");
    const everyRowOffer = wrapper.querySelector("[data-bulk-all-offer]");
    const exporter = wrapper.querySelector("[data-bulk-export]");
    if (!bar) return;

    const rows = () => wrapper.querySelectorAll("[data-bulk-row]");

    const sync = () => {
      const all = rows();
      const picked = Array.from(all).filter((box) => box.checked).length;
      const wholePage = all.length > 0 && picked === all.length;

      bar.hidden = picked === 0;
      if (everyRow && !wholePage) everyRow.disabled = true;
      const widened = everyRow ? !everyRow.disabled : false;
      if (everyRowOffer) {
        everyRowOffer.hidden = !wholePage;
        everyRowOffer.textContent = widened
          ? everyRowOffer.dataset.undoLabel
          : everyRowOffer.dataset.offerLabel;
      }

      const noun = count?.dataset.bulkNoun || "record";
      const plural = count?.dataset.bulkNounPlural || `${noun}s`;
      const size = `${picked} ${picked === 1 ? noun : plural}`;
      if (count) {
        count.textContent = widened
          ? everyRow.dataset.bulkAllLabel
          : `${size} selected`;
      }
      wrapper
        .querySelectorAll("[data-bulk-confirm-title]")
        .forEach((button) => {
          button.dataset.confirmTitle = button.dataset.bulkConfirmTitle.replace(
            "{selection}",
            widened ? everyRow.dataset.bulkAllPhrase : size,
          );
        });
      if (selectAll) {
        selectAll.checked = wholePage;
        // Partial selection reads as neither on nor off, which is what
        // indeterminate is for — without it the header box lies.
        selectAll.indeterminate = picked > 0 && picked < all.length;
      }
    };

    if (selectAll) {
      selectAll.addEventListener("change", () => {
        rows().forEach((box) => {
          box.checked = selectAll.checked;
        });
        sync();
      });
    }

    if (exporter) {
      exporter.addEventListener("click", () => {
        const post = document.createElement("form");
        post.method = "post";
        post.action = exporter.dataset.bulkExport;
        post.hidden = true;
        const add = (name, value) => {
          const input = document.createElement("input");
          input.type = "hidden";
          input.name = name;
          input.value = value;
          post.append(input);
        };
        rows().forEach((box) => {
          if (box.checked) add(box.name, box.value);
        });
        if (everyRow && !everyRow.disabled) add(everyRow.name, everyRow.value);
        document.body.append(post);
        post.submit();
        post.remove();
      });
    }

    if (everyRow && everyRowOffer) {
      everyRowOffer.addEventListener("click", () => {
        everyRow.disabled = !everyRow.disabled;
        sync();
      });
    }

    // Delegated so rows swapped in by a filter change stay wired.
    wrapper.addEventListener("change", (event) => {
      if (event.target.matches("[data-bulk-row]")) sync();
    });
    wrapper.addEventListener("bulk:sync", sync);

    sync();
  });
}
